import $ from 'jquery'
import _ from 'lodash'
import selects from 'scoreboard/components/selects'
import i18n from 'scoreboard/components/i18n'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import Dataset, {
	getRecordEditedField,
	getRecordIsEditableField
} from 'scoreboard/datasets/model/dataset'
const { ALL_TIME, TOTAL, SNAPSHOT, ANY_FILTER } = Dataset
import unwrapModel from 'scoreboard/components/unwrap-model'
import fieldAggregationDescription from 'scoreboard/datasets/field-aggregation-description'
import arrayToHash from 'scoreboard/components/array-to-hash'
import wrapModel from 'scoreboard/components/wrap-model'
import dataTypes from 'scoreboard/datasets/edit/data-types'
import { isJunctionLinkField } from '../forms/dataset-field-filter'
import DataType from 'scoreboard/datasets/model/data-type'
const { DATASET_LINK } = DataType

/**
 * Renders a filtering select of dataset fields, including field groups. Visible
 * linked fields are rendered with an ID equal to -1 * the visible linked
 * field's ID (NOT the field ID, which wouldn't necessarily be unique since a
 * field can be linked to in multiple ways).
 *
 * @param {Object} options
 *
 * @param {Element|jQuery} options.el the SELECT element in the DOM
 *
 * @param {Dataset} options.dataset the dataset
 *
 * @param {Array} [options.allowedFieldIds] the IDs of fields which can be
 * rendered (if omitted, all fields from the current dataset are displayed along
 * with the appropriate fields from linked datasets)
 *
 * @param {Array} [options.allowedVisibleLinkedFieldIds] the IDs of the visible
 * linked fields which can be rendered (if omitted, all visible linked fields
 * will be included as long as filterVisibleLinkedFields=false)
 *
 * @param {String|Number|Array} options.selectedId the initially selected value for the select
 *
 * @param {Object} [options.selectOptions] any options to pass to the scoreboard-select
 *
 * @param {Boolean} [options.required=false] indicates the select should be required
 *
 * @param {Boolean} [options.showDatasetLabel=true] indicates the dataset name should be
 * displayed for fields that aren't in the given dataset
 *
 * @param {Boolean} [options.includeAllTime=false] indicates that "All Time" should be an option
 *
 * @param {Boolean} [options.includeTotal=false] indicates that "Total X"
 * options may be shown in the select if they were previously added as visible
 * linked fields.
 *
 * @param {Boolean} [options.includeAnyFilter=false] Include a "Has any permission filter" option
 *
 * @param {Boolean} [options.addLinkedTotal=false] indicates that a "Total X"
 * option should be added for the given dataset. Only used when editin field
 * equations
 *
 * @param {Boolean} [options.includeNativeTotal] whether
 * to add a "Number of X" option for the native root dataset
 *
 * @param {Boolean} [options.includeSnapshot=false] indicates that "Snapshot" should be an option
 *
 * @param {Boolean} [options.autofocus=false] whether to autofocus the select
 *
 * @param {Boolean} [options.filterVisibleLinkedFields=true] whether to
 * filter the options to only the VLFs specified by allowedVisibleLinkedFieldIds.
 * If this is set to false, all IDs in the dropdown will be regular field IDs
 * instead of field identifiers which are a mix of field IDs and -1 * visibleLinkedFieldId
 *
 * @param {Object} [options.markedFieldOptions] options to pass into the marked
 * field (only applicable if required=true)
 *
 * @param {String} [options.defaultGroup='headerless'] A group value to apply to
 * all the dataset fields (only if they don't already have one).
 *
 * @param {Object[]} [options.extraOptions=[]) If there are other select options
 * that need to be included that can not be derived from the dataset fields.
 * required attributes for each option: {id, label}
 *
 * @param {Object} [options.groups] optional groups to assign to fields. the key
 * should be the field id and the value should be the group name
 * have that group
 *
 * @param {Boolean} [options.excludeLinkedDatasetStyle] if true, linked dataset
 * fields will not have special styling that also lists the dataset it comes from
 * @return {MarkedField} the MarkedField if required=true, or undefined otherwise
 *
 * @param {Boolean} [options.includeEditFields=false] whether to include the
 * "Record is Editable" and "Record Edited" fields in the dropdown (#54607)
 */
function renderFieldDropdown({ el, dataset, allowedFieldIds, selectedId, selectOptions,
		required=false, showDatasetLabel=true, includeAllTime, includeTotal=false, autofocus,
		filterVisibleLinkedFields=true, markedFieldOptions, includeSnapshot=false,
		allowedVisibleLinkedFieldIds, includeNativeTotal, addLinkedTotal=false,
		includeAnyFilter=false, defaultGroup='headerless',
		extraOptions = [], groups = {}, excludeLinkedDatasetStyle = false,
		includeEditFields = false }) {
	dataset = wrapModel(dataset, Dataset)
	let fields = filterVisibleLinkedFields ? dataset?.get('fields') :
		dataset?.combinedFields
	// #25508 don't mutate the list of fields in the dataset!
	fields = _.cloneDeep(fields)

	if (filterVisibleLinkedFields) {
		let allowedVisibleLinkedFieldIdsHash = arrayToHash(allowedVisibleLinkedFieldIds)
		let visibleLinkedAdditions = _.filter(dataset?.get('visibleLinkedFields'), vlf => {
			if (!includeTotal && Dataset.isTotal(vlf.fieldId)) {
				return false
			}
			if (allowedVisibleLinkedFieldIds) {
				return allowedVisibleLinkedFieldIdsHash[vlf.id]
			} else {
				return true
			}
		})
		fields?.push(... _.map(visibleLinkedAdditions, vlf =>
			dataset.getVisibleLinkedFieldOption(vlf.id)))
	}

	//don't pass allowedVisibleLinkedFieldIds since we're doing it above
	const optionDataOptions = {
		fields, dataset, allowedFieldIds, allowedVisibleLinkedFieldIds,
		showDatasetLabel, includeAllTime, includeTotal,
		includeSnapshot, includeNativeTotal, addLinkedTotal,
		includeAnyFilter, defaultGroup,
		extraOptions, groups
	}
	const optionData = fieldsToOptions(optionDataOptions)
	includeEditFields && addEditFields(optionData.options)

	selects.filtering(el, _.defaults(selectOptions, {
		autofocus,
		accessors: {
			value: 'id'
		},
		fullHeight: true, // #13497
		placeholder: i18n.get('datasets.fields.choose'),
		selected: selectedId,
		render: {
			option(d, searchTerm) {
				return datasetLabel(excludeLinkedDatasetStyle, d, searchTerm)
			},
			item(d) {
				return datasetLabel(excludeLinkedDatasetStyle, d)
			}
		}
	}, optionData))

	let markedField = $(el).data('view')
	if (required && !markedField) {
		let options = _.extend({
			el,
			validator: requiredValidator
		}, markedFieldOptions)
		markedField = new MarkedField(options).render()
	}
	return markedField
}

/**
 * Adds the "Record is Editable" and "Record Edited" pseudo-fields to the
 * options list
 * @param {Array} options the options which will be mutated to include the
 * pseudo-fields
 */
function addEditFields(options) {
	let newOptions = [ getRecordIsEditableField(),
		getRecordEditedField() ]
	_.each(newOptions, newOption => {
		_.extend(newOption, {
			customIcon:  'yes-no-data-type',
			sortGroup: 'headerless',
			group: 'headerless',
			sortSpecialFields: getSortSpecialFields(newOption),
			sortLabel: getSortLabel(newOption)
		})
	})

	options.push(...newOptions)
}

function datasetLabel(excludeLinkedDatasetStyle, d, searchTerm) {
	let linkedDataset = d.linkedDataset && !excludeLinkedDatasetStyle
		? `<span class="linked-dataset-label">${d.linkedDataset}</span>` : ''
	let linkedFieldLabel = d.linkedDataset && !excludeLinkedDatasetStyle
		? 'linked-field-label' : ''

	// For the selected item (not in menu).
	// If linked dataset label is needed, append it after the field-label so there is room for the cursor
	if (d.selected && !d.highlighted) {
		return `<span class="${linkedFieldLabel}">${d.label}</span>${linkedDataset}`
	}

	// If linked dataset label is needed, put it at the end so it can fill the
	// remaining space left by the field-label.
	return `${selects.optionHtml(d, searchTerm, {cssClasses: linkedFieldLabel})}${linkedDataset}`
}

/**
 * External API for getting a dataset's fields as a list of options that can
 * be used in a dropdown or other type of UI list. This list only shows direct
 * fields and VLF's. It does not include other linked fields.
 *
 * @param {Dataset} dataset the dataset to get the fields from
 * @param {Object} options various options that will be passed to `convertFieldsToOptions`
 * @param {number[]?} options.allowedFieldIds ids of fields to include in the list. Default
 * is to allow all fields
 * @param {number[]?} options.allowedVisibleLinkedFieldIds ids of visible linked fields to
 * include. Default is to allow all.
 */
function datasetFieldsToOptions(dataset, options = {}) {
	const vlfsByFieldIds = _.keyBy(_.values(dataset.visibleLinkedFieldsById), 'fieldId')
	const numberOfVLFs = _.filter(_.values(dataset.visibleLinkedFieldsById),
		f => Dataset.isTotal(f.fieldId)) ?? []

	const fields = _.cloneDeep(_.concat(dataset.combinedFields, numberOfVLFs))
		.filter(f => {
			const isNumberOfVLF = _.some(numberOfVLFs, vlf => vlf.id == f.id)
			const vlf = isNumberOfVLF ? _.cloneDeep(f) : vlfsByFieldIds[f.id]

			// Don't allow fields that aren't explicitly assigned as a visible
			// linked field
			if (!isNumberOfVLF &&
				(!vlf && !_.isNil(f.linkedDatasetId) && f.datasetId != dataset.get('rootDatasetId'))) return false

			if (vlf) {
				// Convert id to fieldIdentifier so the correct label is used
				f.id = Dataset.getFieldIdentifier(f.id, vlf.id)
			}
			const acceptableFieldIds = vlf
				? options.allowedVisibleLinkedFieldIds
				: options.allowedFieldIds
			const fieldId = vlf ? vlf.id : f.id
			return !acceptableFieldIds || _.includes(acceptableFieldIds, fieldId)
		})

	return convertFieldsToOptions(dataset, fields, options)
}

/**
 * This is here for backwards compatability. Use {@link datasetFieldsToOptions}
 * instead
 * @deprecated
 */
function fieldsToOptions({ fields, dataset, allowedFieldIds, allowedVisibleLinkedFieldIds,
	 showDatasetLabel=true, includeAllTime, includeTotal=false,
	 includeSnapshot=false, includeNativeTotal=false, addLinkedTotal=false,
	 includeAnyFilter=false, defaultGroup='headerless',
	 extraOptions = [], groups = {}, include}) {
	dataset = wrapModel(dataset, Dataset)

	//determine whitelist hashes
	let allowedFieldIdsHash = allowedFieldIds ? arrayToHash(allowedFieldIds) : null
	let allowedVLFIdsHash = allowedVisibleLinkedFieldIds ? arrayToHash(allowedVisibleLinkedFieldIds) : null
	if (allowedFieldIdsHash || allowedVLFIdsHash) {
		fields = _.filter(fields, f => {

			//if this is a vlf id, check to see if it's allowed in the VLF hash
			if (Dataset.isVisibleLinkedFieldIdentifier(f.id)) {
				if (allowedVLFIdsHash) {
					let { visibleLinkedFieldId } = dataset.fieldIdentifierToIds(f.id)
					return allowedVLFIdsHash[visibleLinkedFieldId]
				} else {
					return true	//no whitelist, default to is to allow
				}
			}

			//if field id whitelist is defined check to see this field is allowed
			if (allowedFieldIdsHash) {
				//the field id of a vlf may also be specified instead of the vlf's
				//fieldIdentifier, so allow the vlf in that way as well
				return allowedFieldIdsHash[f.id] || allowedFieldIdsHash[f.fieldId]
			}
			return true	//no whitelist, default is to allow
		})
	}

	return convertFieldsToOptions(dataset, fields, {
		showDatasetLabel, includeAllTime, includeTotal,
		includeSnapshot, includeNativeTotal, addLinkedTotal, includeAnyFilter,
		defaultGroup, extraOptions, groups
	})
}

function convertFieldsToOptions(dataset, fields, { showDatasetLabel = true,
	includeAllTime = false, includeTotal = false,
	includeSnapshot = false, includeNativeTotal = false,
	addLinkedTotal = false, includeAnyFilter = false,
	defaultGroup = 'headerless', extraOptions = [], groups = {} }) {
	if (includeAllTime) {
		fields.push({
			id: ALL_TIME,
			label: i18n.get('common.allTime')
		})
	}
	// the total option for linked datasets
	if (addLinkedTotal) {
		fields.push({
			id: TOTAL,
			label: i18n.get('datasets.total.tracked', dataset.get('tracked'))
		})
	}
	// the total option for the native (non-linked) dataset
	if (includeNativeTotal) {
		fields.push({
			id: TOTAL,
			label: fieldAggregationDescription({
				dataset,
				tracked: dataset.get('tracked')
			})
		})
	}
	if (includeSnapshot) {
		fields = [{
			id: SNAPSHOT,
			label: i18n.get('datasets.metric.snapshot.option'),
			icon: 'not-allowed'
		}].concat(fields)
	}

	if (includeAnyFilter) {
		fields = [{
			id: ANY_FILTER,
			label: i18n.get('datasets.permissions.restrictiveFilter.any'),
			group: i18n.get('common.all'),
			icon: ''
		}].concat(fields)
	}

	let options = _(fields)
		.filter(field => {
			// always allow any pseudo fields to be included
			if (field.id <= 0) {
				return true
			}
			// #16373 always filter the fields to hide any that shouldn't be visible
			if (!dataset.isModifyDataset() && field.hideFromNonAdmins) {
				return false
			}
			return true
		})
		// #18394 no need to sort the options here; sifter takes care of that
		// using the 'sort' option set below
		.map(field => {
			let label = dataset.getFieldLabel(field)
			let datasetForField = unwrapModel(dataset.getDatasetForField(field))
			let matchingRootDataset =
				_.get(unwrapModel(dataset), 'rootDatasetId') ==
				_.get(unwrapModel(datasetForField), 'rootDatasetId')
			let linkedDataset = showDatasetLabel && datasetForField &&
			!matchingRootDataset ? datasetForField.label : false
			let group = groups[field.id] || field.group || defaultGroup
			let dataType = field.dataType
			if (isJunctionLinkField(field, dataset)) {
				// #45989 A junction link isn't *technically* a dataset link
				// itself, but it is one part of a 3 part link
				dataType = DATASET_LINK
			}
			let dataTypeById = _.find(dataTypes, ['id', dataType])
			// If no data type was matched that indicates it's a
			// Total field (e.g. Number of Sexual Assaults),
			// so the correct dataType is number.
			let customIcon
			if (!_.isNil(field.icon)) {
				customIcon = field.icon
			} else {
				customIcon = dataTypeById ? dataTypeById.icon : 'number'
			}

			let optionData = _.defaults({
				label,
				linkedDataset,
				group: group,
				sortSpecialFields: getSortSpecialFields(field),
				sortLabel: getSortLabel(field),
				sortGroup: group.toLocaleLowerCase(),
				customIcon
			}, field)
			return optionData
		})
		.value()

	extraOptions = _.map(extraOptions, o => {
		const sortGroup = defaultGroup.toLocaleLowerCase()
		return _.defaults({
			group: defaultGroup,
			sortSpecialFields: 1,
			sortLabel: getSortLabel(o),
			sortGroup
		}, o)
	})

	options = _.concat(options, extraOptions)
	let groupBy = undefined
	const allGroups = getFieldGroups(fields, groups)
	if (!_.isEmpty(allGroups) && allGroups.length > 1) {
		groupBy = 'group'
	}

	return {
		options,
		groupBy,
		sort: [
			// All Time option is always first (if present)
			{ field: 'sortSpecialFields', direction: 'asc' },
			// Then sort by group, so that the groups will show up in alphabetical order
			{ field: 'sortGroup', direction: 'asc' },
			// Finally, sort by label, so that the fields will be in alphabetical order
			// within the groups
			// #18394 'label' is HTML for linked fields, so we can't sort on that.
			// Instead, use the 'sortLabel' created above
			{ field: 'sortLabel', direction: 'asc' }
		]
	}
}

/**
 * Returns the label used to sort an option shown in the field dropdown
 * @param {Object} option the option to get the sort label for
 */
function getSortLabel(option) {
	return option.label.toLocaleLowerCase()
}

/**
 * Returns the value for the "sortSpecialFields" property based on one of the
 * options in the dropdown. This is used to ensure that certain items always
 * appear at the top of the list (e.g "All Time" or "Total X").
 */
function getSortSpecialFields(option) {
	return Dataset.isPseudoOption(option.id) ? 0 : 1
}

function getFieldGroups(fields, groups) {
	return _(fields)
		// "headerless" just means no group
		.map(f => _.get(groups, `[${f.id}]`) || f.group || 'headerless')
		.uniq()
		.orderBy(group => group.toLocaleUpperCase())
		.value()
}

_.extend(renderFieldDropdown, {
	datasetFieldsToOptions
})
export default renderFieldDropdown
export { datasetFieldsToOptions, addEditFields }
renderFieldDropdown.__filename__ = 'datasets/render-field-dropdown.js'
